import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@src/container';
import { AuthService } from '@src/application/users/usecases';
import { ToastService } from '@src/shared/services/toast.service';

/**
 * Hook: useSetName
 * Sets user's name during onboarding
 * Only works when profile_state is NAME_REQUIRED
 * 
 * @example
 * const { mutate: setName, isPending } = useSetName();
 * setName({ firstName: 'John', lastName: 'Doe', middleName: 'Michael' });
 */
export function useSetName() {
    const authService = container.resolve<AuthService>('authService');
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ firstName, lastName, middleName }: { firstName: string; lastName: string; middleName?: string }) => {
            return await authService.setName(firstName, lastName, middleName);
        },
        onSuccess: () => {
            // Invalidate current user to refresh profile state
            queryClient.invalidateQueries({ queryKey: ['auth', 'currentUser'] });
            ToastService.success('Name set successfully! Welcome to PascoHub!');
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : 'Failed to set name';
            ToastService.error(errorMessage);
        },
        retry: false,
    });
}
